import React from 'react';
import {observer} from 'mobx-react';
import {TechseeBaseComponent} from '@techsee/techsee-ui-common/lib/_shared/techsee-base-component';
import {requireImage} from '../../utils/utils';
import {ITsImageEditToolsController} from './ts-image-edit-tools.controller';
import Translate from '../translate/Translate';

import './ts-image-edit-tools.styles.scss';
import {IMAGE_MODES} from '../../../states/dashboard/main/main.settings';

interface TsImageEditToolsProps {
    controller: ITsImageEditToolsController;
}

function requireSelectableImage(iconName: string, useNewInterface: boolean, isSelected: boolean) {
    const suffix = isSelected ? '-selected' : '';
    return requireImage(`${iconName}${suffix}.png`, useNewInterface);
}

@observer
export class TsImageEditTools extends TechseeBaseComponent<TsImageEditToolsProps> {
    renderInternal(): JSX.Element {
        const {controller} = this.props;
        const {showScanners, imageMode, useNewInterface} = controller.config;

        return (
            <>
                {/* Cancel & Undo */}
                <div className='dashboard-main-bar-group pull-left'>
                    <a
                        href='#'
                        onClick={(e) => {
                            e.preventDefault();
                            controller.cancel();
                        }}
                        className='main-text-color'>
                        <span className='dashboard-main-action-icon'>
                            <img src={requireImage('cancel.png', useNewInterface)} alt='Cancel' />
                        </span>
                        <span className='dashboard-main-bar-label'>
                            <Translate text={'MAIN.VIEW.CANCEL'} />
                        </span>
                    </a>
                    <a
                        href='#'
                        onClick={(e) => {
                            e.preventDefault();
                            controller.undo();
                        }}
                        className={`main-text-color ${!controller.hasAnnotations ? 'disabled-annotation-button' : ''}`}>
                        <span className='dashboard-main-action-icon'>
                            <img src={requireImage('undo.png', useNewInterface)} alt='Undo' />
                        </span>
                        <span className='dashboard-main-bar-label'>
                            <Translate text={'MAIN.VIEW.UNDO'} />
                        </span>
                    </a>
                </div>

                {/* Zoom Image */}
                <div className='dashboard-main-bar-group pull-left'>
                    <div className='dropdown'>
                        <a
                            href='#'
                            onClick={(e) => {
                                e.preventDefault();
                                controller.toggleZoomDropdown();
                            }}
                            className={controller.zoomEnabled ? '' : 'disabled'}
                            title='Zoom'>
                            <span className='dashboard-main-action-icon dashboard-big-icon'>
                                <img src={requireImage('icon-zoom.png', useNewInterface)} alt='Zoom' />
                            </span>
                            <span className='dashboard-main-bar-label'>
                                <Translate text={'MAIN.VIEW.ZOOM'} />
                            </span>
                        </a>
                        <div
                            className={`dropdown-menu zoom-slider ${controller.isZoomDropdownOpen ? 'show' : ''}`}
                            onWheel={(e: React.WheelEvent<HTMLDivElement>) => {
                                controller.wheelChangeZoom(e.nativeEvent, (e as any).deltaY > 0 ? 1 : -1);
                            }}>
                            <div className='slider-container'>
                                <input
                                    type='range'
                                    value={controller.zoomFactor}
                                    onInput={(e) => {
                                        controller.handleZoomInput(e.currentTarget.value);
                                    }}
                                    onChange={(e) => {
                                        controller.handleZoomInput(e.currentTarget.value);
                                    }}
                                    min='1'
                                    max='5'
                                    step='0.1'
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Rotation Tools */}
                <div className='dashboard-main-bar-group dashboard-main-rotation-group'>
                    <div className='dashboard-main-rotation-buttons'>
                        <a
                            href='#'
                            onClick={(e) => {
                                e.preventDefault();
                                controller?.rotateLeft();
                            }}
                            className={controller?.rightRotateEnabled ? '' : 'disabled'}
                            title='Rotate Left'>
                            <span className='dashboard-main-action-icon'>
                                <img src={requireImage('icon-rotate-left.png', useNewInterface)} alt='Rotate Left' />
                            </span>
                        </a>
                        <a
                            href='#'
                            onClick={(e) => {
                                e.preventDefault();
                                controller?.rotateRight();
                            }}
                            className={controller?.leftRotateEnabled ? '' : 'disabled'}
                            title='Rotate Right'>
                            <span className='dashboard-main-action-icon'>
                                <img src={requireImage('icon-rotate-right.png', useNewInterface)} alt='Rotate Right' />
                            </span>
                        </a>
                    </div>
                    <div
                        className={`dashboard-main-bar-label text-center ${
                            !controller.rightRotateEnabled && !controller.leftRotateEnabled ? 'disabled' : ''
                        }`}>
                        <Translate text={'MAIN.VIEW.ROTATE'} />
                    </div>
                </div>

                {/* Scan Tools */}
                {showScanners && (
                    <div className='dashboard-main-bar-group'>
                        {controller.scanCaptureToggleEnabled ? (
                            <a
                                href='#'
                                onClick={(e) => {
                                    e.preventDefault();
                                    controller.scan();
                                }}
                                className={`main-text-color ${
                                    imageMode === IMAGE_MODES.SCANNING
                                        ? 'dashboard-main-bar-item-selected disable-dropdown disabled'
                                        : ''
                                }`}
                                title='Scan Image'>
                                <span className='dashboard-main-action-icon'>
                                    <img
                                        src={requireSelectableImage(
                                            'scan',
                                            useNewInterface,
                                            imageMode === IMAGE_MODES.SCANNING
                                        )}
                                        alt='Scan'
                                    />
                                </span>
                                <span className='dashboard-main-bar-label'>
                                    <Translate text={'MAIN.VIEW.SCAN'} />
                                </span>
                            </a>
                        ) : (
                            <div className='dropdown'>
                                <a
                                    href='#'
                                    onClick={(e) => {
                                        e.preventDefault();
                                        controller.selectedScanMode();
                                    }}
                                    className={`main-text-color ${
                                        imageMode === IMAGE_MODES.SCANNING
                                            ? 'dashboard-main-bar-item-selected disable-dropdown disabled'
                                            : ''
                                    }`}
                                    title='Scan Image'>
                                    <span className='dashboard-main-action-icon'>
                                        <img
                                            src={requireSelectableImage(
                                                'scan',
                                                useNewInterface,
                                                imageMode === IMAGE_MODES.SCANNING
                                            )}
                                            alt='Scan'
                                        />
                                    </span>
                                    <span className='dashboard-main-bar-label'>
                                        <Translate text={'MAIN.VIEW.SCAN'} />
                                    </span>
                                </a>
                            </div>
                        )}
                    </div>
                )}
            </>
        );
    }
}
